/**
 * Morse — „Eine Stufe zurück“ und „Stufe wählen“. (08.10.2026)
 *
 * Martin: „Gibt es die Möglichkeit, wenn man zu schnell voranschreitet, dass man wieder ein Level zurückschalten
 * kann? Mir ist jetzt ein Buchstabe zu viel.“ Bis dahin gab es nur den Aufstieg.
 *
 * EIN Baustein, zwei Plätze:
 *   - Fortschritt (voll): ein Block mit Erklärung, „Eine Stufe zurück“ und „Stufe wählen“ (bis zur höchsten erreichten);
 *   - Hören (kompakt): nach einem Aufstieg eine schlichte Zeile „Zu viel auf einmal? [Eine Stufe zurück]“.
 *
 * Jede Änderung geht über dieselbe Frage im Seitentext — kein Browser-Dialog: ein Satz, was passiert („Stufe 3 → 2:
 * R fällt vorerst heraus, dein Stand bleibt gespeichert.“), dann „Ja, Stufe 2 nehmen“ oder „Abbrechen“. Danach steht
 * ein Satz, was jetzt gilt und wie man es rückgängig macht („Stufe wählen“). Der Stand der Seite folgt der Antwort des
 * Servers (morse-view.ts); hier wird nichts gerechnet, was der Server entscheidet.
 *
 * Mitten in einer Runde (läuft oder ruht) ist alles gesperrt — mit einem Satz, warum (`StufeAnschluss.sperre`).
 *
 * ⚠️ Keine Inline-Styles: alles in style.css (`.morse-stufe…`).
 */
import { kochNeueZeichen, kochStufenWechsel } from "../../shared/morse-koch.js";
import { STUFE } from "./morse-texte.js";
import { baueMeldung, el, hoechsteStufeVon, knopf, meldeIn, type MorseStandDaten, type StufeAnschluss } from "./morse-teile.js";

let zaehler = 0;

interface StufeOptionen {
  /** Nur die schlichte Zeile mit „Eine Stufe zurück“ (Hören); sonst der Block mit Erklärung und „Stufe wählen“. */
  kompakt?: boolean;
  /** Die Stufe wurde geändert — wer eine alte Aussage zeigt („Stufe 4! Neu dazu …“), nimmt sie jetzt weg. */
  beiWechsel?: () => void;
}

export class MorseStufe {
  readonly wurzel: HTMLElement;
  private readonly kompakt: boolean;
  private readonly zurueckKnopf: HTMLButtonElement;
  private readonly wahlFeld: HTMLElement | null = null;
  private readonly auswahl: HTMLSelectElement | null = null;
  private readonly wahlHinweis: HTMLElement | null = null;
  private readonly frage: HTMLElement;
  private readonly satz: HTMLElement;
  private readonly jaKnopf: HTMLButtonElement;
  private readonly neinKnopf: HTMLButtonElement;
  private readonly meldung: HTMLElement;
  private readonly sperrZeile: HTMLElement;
  private stand: MorseStandDaten | null = null;
  /** Die Stufe, nach der gerade gefragt wird (`null`: keine Frage offen). */
  private ziel: number | null = null;
  /** Wer die Frage geöffnet hat — dorthin kehrt der Fokus bei „Abbrechen“ zurück. */
  private ausloeser: HTMLElement | null = null;
  private beschaeftigt = false;
  private optionen = "";

  constructor(
    private readonly anschluss: StufeAnschluss,
    private readonly o: StufeOptionen = {},
  ) {
    const nr = ++zaehler;
    this.kompakt = o.kompakt === true;
    this.wurzel = el(this.kompakt ? "div" : "section", this.kompakt ? "morse-stufe morse-stufe--zeile" : "morse-block morse-stufe");

    const aktionen = el("div", "morse-stufe-aktionen");
    this.zurueckKnopf = knopf(STUFE.zurueck, "morse-knopf", () => this.fragen(this.stand!.stufe - 1, this.zurueckKnopf));

    if (this.kompakt) {
      aktionen.append(el("span", "morse-stufe-zeile-text", STUFE.zeile), this.zurueckKnopf);
      this.wurzel.appendChild(aktionen);
    } else {
      const titel = el("h3", "morse-block-titel", STUFE.titel);
      titel.id = `morse-stufe-titel-${nr}`;
      this.wurzel.setAttribute("aria-labelledby", titel.id);
      aktionen.appendChild(this.zurueckKnopf);
      this.wahlFeld = el("label", "morse-stufe-wahl");
      this.auswahl = el("select", "morse-stufe-auswahl");
      this.auswahl.addEventListener("change", () => this.beiAuswahl());
      this.wahlFeld.append(el("span", "morse-feld-titel", STUFE.wahlTitel), this.auswahl);
      aktionen.appendChild(this.wahlFeld);
      this.wahlHinweis = el("p", "morse-hinweis morse-stufe-hinweis");
      this.wurzel.append(titel, el("p", "morse-hinweis", STUFE.erklaerung), aktionen, this.wahlHinweis);
    }

    // Die Frage: ein Satz, was passiert, und die Wahl dazu.
    this.frage = el("div", "morse-stufe-frage");
    this.frage.hidden = true;
    this.frage.setAttribute("role", "group");
    this.satz = el("p", "morse-stufe-satz");
    this.satz.id = `morse-stufe-satz-${nr}`;
    this.frage.setAttribute("aria-labelledby", this.satz.id);
    this.jaKnopf = knopf("", "morse-knopf morse-knopf--voll", () => void this.bestaetigen());
    this.neinKnopf = knopf(STUFE.abbrechen, "morse-knopf", () => this.abbrechen());
    const antworten = el("div", "morse-aktionen");
    antworten.append(this.jaKnopf, this.neinKnopf);
    this.frage.append(this.satz, antworten);
    this.frage.addEventListener("keydown", (ev) => {
      if (ev.key !== "Escape") return;
      ev.preventDefault();
      ev.stopPropagation();
      this.abbrechen();
    });

    this.sperrZeile = el("p", "morse-hinweis morse-stufe-sperre", STUFE.sperre);
    this.sperrZeile.id = `morse-stufe-sperre-${nr}`;
    this.sperrZeile.hidden = true;
    this.meldung = baueMeldung();
    this.meldung.tabIndex = -1;
    this.wurzel.append(this.sperrZeile, this.frage, this.meldung);
  }

  /** Den Stand zeigen: Knöpfe, Wahl, Sperre. Eine offene Frage bleibt, solange sie noch stimmt. */
  zeichne(s: MorseStandDaten): void {
    this.stand = s;
    const hoechste = hoechsteStufeVon(s);
    const sperre = this.anschluss.sperre();
    const aus = sperre !== null || this.beschaeftigt;
    // Der Block im Fortschritt zeigt sich nur, wo es etwas zu wählen gibt (die Zeile beim Hören bestimmt der Reiter).
    if (!this.kompakt) this.wurzel.hidden = hoechste < 2;
    this.zurueckKnopf.hidden = s.stufe < 2;
    this.zurueckKnopf.disabled = aus;
    if (this.auswahl && this.wahlFeld && this.wahlHinweis) {
      this.wahlFeld.hidden = hoechste < 2;
      this.wahlHinweis.hidden = hoechste < 2;
      this.wahlHinweis.textContent = STUFE.wahlHinweis(hoechste);
      const schluessel = `${s.stufe}/${hoechste}`;
      if (schluessel !== this.optionen) {
        this.optionen = schluessel;
        this.auswahl.textContent = "";
        for (let n = 1; n <= hoechste; n++) {
          const opt = el("option", "", STUFE.option(n, kochNeueZeichen(n), n === s.stufe));
          opt.value = String(n);
          this.auswahl.appendChild(opt);
        }
      }
      if (this.ziel === null) this.auswahl.value = String(s.stufe);
      this.auswahl.disabled = aus;
    }
    // Gesperrt: die Knöpfe sind aus, der Satz steht daneben — und hängt als Beschreibung an ihnen.
    this.sperrZeile.hidden = sperre === null || hoechste < 2;
    for (const e of [this.zurueckKnopf, this.auswahl]) {
      if (!e) continue;
      if (sperre !== null) e.setAttribute("aria-describedby", this.sperrZeile.id);
      else e.removeAttribute("aria-describedby");
    }
    // Eine offene Frage, die nicht mehr stimmt (Stufe schon erreicht, Runde offen, Ziel unerlaubt), wird geschlossen.
    if (this.ziel !== null) {
      if (this.ziel === s.stufe || this.ziel < 1 || this.ziel > hoechste || sperre !== null) this.schliesse();
      else this.zeigeFrage(this.ziel);
    }
  }

  /** Alles Offene wegnehmen: die Frage und die Meldung (wer die Zeile neu zeigt, beginnt sauber). */
  zuruecksetzen(): void {
    this.schliesse();
    meldeIn(this.meldung, "");
  }

  private beiAuswahl(): void {
    const v = Number(this.auswahl!.value);
    if (!this.stand || !Number.isInteger(v)) return;
    if (v === this.stand.stufe) {
      this.schliesse();
      return;
    }
    this.fragen(v, this.auswahl!);
  }

  private fragen(ziel: number, ausloeser: HTMLElement): void {
    if (!this.stand) return;
    const sperre = this.anschluss.sperre();
    if (sperre !== null) {
      this.zeichne(this.stand);
      return;
    }
    this.ausloeser = ausloeser;
    this.ziel = ziel;
    meldeIn(this.meldung, "");
    this.zeigeFrage(ziel);
    this.jaKnopf.focus();
  }

  private zeigeFrage(ziel: number): void {
    const von = this.stand!.stufe;
    const w = kochStufenWechsel(von, ziel);
    this.satz.textContent = STUFE.frage(von, ziel, w.fallenWeg, w.kommenDazu);
    this.jaKnopf.textContent = STUFE.ja(ziel);
    this.jaKnopf.disabled = this.beschaeftigt;
    this.neinKnopf.disabled = this.beschaeftigt;
    this.jaKnopf.setAttribute("aria-describedby", this.satz.id);
    this.frage.hidden = false;
  }

  private schliesse(): void {
    this.ziel = null;
    this.frage.hidden = true;
    if (this.auswahl && this.stand) this.auswahl.value = String(this.stand.stufe);
  }

  private abbrechen(): void {
    const zurueck = this.ausloeser;
    this.schliesse();
    if (zurueck && !zurueck.hidden) zurueck.focus();
  }

  private async bestaetigen(): Promise<void> {
    const bis = this.ziel;
    const stand = this.stand;
    if (bis === null || !stand || this.beschaeftigt) return;
    const von = stand.stufe;
    this.beschaeftigt = true;
    this.zeichne(stand);
    let r: Awaited<ReturnType<StufeAnschluss["setze"]>>;
    try {
      r = await this.anschluss.setze(bis);
    } catch {
      r = { ok: false, fehler: "Keine Verbindung zum Server." };
    }
    this.beschaeftigt = false;
    this.schliesse();
    if (r.ok) {
      const w = kochStufenWechsel(von, bis);
      meldeIn(this.meldung, STUFE.fertig(von, bis, w.fallenWeg, w.kommenDazu, !this.kompakt), "gut");
      this.o.beiWechsel?.();
    } else {
      meldeIn(this.meldung, STUFE.fehler(r.fehler), "fehler");
    }
    // Der Stand der Seite ist (bei Erfolg) schon neu; Knöpfe wieder frei, Fokus dorthin, wo man weitermacht.
    if (this.stand) this.zeichne(this.stand);
    this.fokus();
  }

  private fokus(): void {
    const kandidaten: Array<HTMLElement | null> = [this.zurueckKnopf, this.auswahl];
    const ziel = kandidaten.find((e) => e && !e.hidden && !(e as HTMLButtonElement).disabled && e.checkVisibility?.() !== false);
    (ziel ?? this.meldung).focus();
  }
}
